import type { ConversationTopicDto } from '../api/client';
import { effortDisplayName, modelDisplayName, providerDisplayName } from '../lib/model-names';

/** CO.6 — estado, identidade e janela de contexto do assistente da Conversa. */
export function TopicRuntimeStatus({ topic }: { topic: ConversationTopicDto }) {
  const entry = topic.queue.at(-1);
  const working = Boolean(topic.activeTurnTaskRunId);
  const phase = topic.context.runtimeState;
  const lastUpdate = topic.context.lastUpdateAt ?? topic.updatedAt;
  const quietSeconds = Math.max(0, Math.floor((Date.now() - Date.parse(lastUpdate)) / 1000));
  const queued = topic.queue.filter(s => ['queued', 'deferred'].includes(s.status));
  const status = working
    ? phase === 'agent_thought_chunk' ? 'Pensando' : phase === 'agent_message_chunk' ? 'Respondendo'
      : phase === 'tool_call' || phase === 'tool_call_update' ? 'Executando ação' : phase === 'compaction_update' ? 'Compactando contexto' : phase ? 'Trabalhando' : 'Iniciando assistente'
    : topic.status === 'archived' ? 'Arquivada' : queued.length ? 'Aguardando · pedido na fila'
      : topic.context.state !== 'lazy' && entry?.status === 'failed' ? 'Bloqueada · falha na resposta' : 'Ociosa · pronta para conversar';
  const provider = providerDisplayName(topic.context.provider);
  const model = modelDisplayName(topic.context.model);
  const effort = `${effortDisplayName(topic.context.effort ?? topic.context.sessionEffort)}${topic.context.sessionEffort ? ' (esta sessão)' : ''}`;
  const runId = topic.activeTurnTaskRunId ?? entry?.taskRunId;
  const used = topic.context.contextUsed ?? null;
  const size = topic.context.contextSize ?? null;
  const pct = used !== null && size ? Math.min(100, Math.round((used / size) * 100)) : null;
  const compactedAt = topic.context.lastCompactionAt ? new Date(topic.context.lastCompactionAt) : null;
  return <div className="topic-runtime" data-working={working} data-testid="topic-runtime-status">
    <span role="status"><i aria-hidden="true" />{working ? 'Trabalhando · ' : ''}{status}{working && queued.length ? ` · ${queued.length} na fila` : ''}</span>
    <span data-testid="topic-runtime-identity">{provider} · {model} · esforço {effort}</span>
    <span className="topic-context-meter" data-testid="topic-context-meter" title={pct === null ? 'O provedor ainda não informou o uso da janela de contexto nesta sessão.' : `${used?.toLocaleString('pt-BR')} de ${size?.toLocaleString('pt-BR')} tokens em contexto`}>
      {pct === null ? <>contexto: sem leitura ainda</> : <>
        <meter min={0} max={100} low={60} high={85} optimum={20} value={pct} aria-label="uso da janela de contexto" />
        contexto {pct}%{pct >= 85 ? ' · compactação automática próxima' : ''}
      </>}
      {compactedAt ? <em> · compactado às {compactedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</em> : null}
    </span>
    {working && quietSeconds >= 45 ? <span>Sem nova atividade há {Math.floor(quietSeconds / 60)} min {quietSeconds % 60} s</span> : null}
    <details>
      <summary>Detalhes da execução</summary>
      <p>Este estado é do assistente da Conversa. Terminais e o Produto continuam trabalhando enquanto a Conversa está ociosa; acompanhe pelo quadro de apontamentos.</p>
      {runId ? <a href={`/runs?runId=${encodeURIComponent(runId)}`}>Abrir inspeção técnica desta execução</a> : null}
      <dl>
        <dt>Execução</dt><dd>{topic.activeTurnTaskRunId ?? entry?.taskRunId ?? 'nenhuma'}</dd>
        <dt>Última atividade</dt><dd>{new Date(lastUpdate).toLocaleString('pt-BR')}</dd>
        <dt>Estado técnico</dt><dd>{working ? phase ?? 'iniciando' : entry?.status ?? 'pronto'}</dd>
        <dt>Modelo (id)</dt><dd>{topic.context.model ?? '—'} · {topic.context.effort ?? '—'}</dd>
        {entry?.lastErrorCode ? <><dt>Causa</dt><dd>{entry.lastErrorCode}</dd></> : null}
      </dl>
    </details>
  </div>;
}
